/*
 * Dialog and Sheet on Radix Dialog: focus is trapped while open and
 * returned to the trigger on close; Escape closes.
 */
import type { ReactNode } from "react";
import { Dialog as R } from "radix-ui";
import { cx } from "../lib/cx";
import { IconX } from "./icons";

type BaseProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Prevent closing on outside click/Escape, e.g. while a secret is on screen. */
  dismissable?: boolean;
  className?: string;
  /** Where focus goes when the dialog opens; defaults to the first focusable element. */
  onOpenAutoFocus?: (e: Event) => void;
  /** Where focus goes when it closes; defaults to what had it before (a dialog opened from a menu item needs the menu's button). */
  onCloseAutoFocus?: (e: Event) => void;
};

/**
 * Focus the first thing to act on when a dialog opens (a field, or the
 * selected option), rather than the close button in its corner.
 */
function focusFirstField(e: Event, custom?: (e: Event) => void) {
  if (custom) return custom(e);
  const root = e.currentTarget as HTMLElement | null;
  const target = root?.querySelector<HTMLElement>(
    "[data-autofocus], [data-dialog-body] input:not([type=hidden]):not([type=radio]):not([disabled]), [data-dialog-body] input[type=radio]:checked, [data-dialog-body] select, [data-dialog-body] textarea",
  );
  if (target) {
    e.preventDefault();
    target.focus();
  }
}

function CloseButton() {
  return (
    <R.Close
      aria-label="Close"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-dim transition-colors hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500"
    >
      <IconX size={17} />
    </R.Close>
  );
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissable = true,
  className,
  size = "md",
  align = "center",
  onOpenAutoFocus,
  onCloseAutoFocus,
}: BaseProps & { size?: "sm" | "md" | "lg" | "xl"; /** "top" keeps the header still when the content changes height. */ align?: "center" | "top" }) {
  const width = { sm: "max-w-[420px]", md: "max-w-[520px]", lg: "max-w-[680px]", xl: "max-w-[860px]" }[size];
  return (
    <R.Root open={open} onOpenChange={onOpenChange}>
      <R.Portal>
        <R.Overlay className="hx-overlay fixed inset-0 z-50 bg-[var(--hx-overlay)] backdrop-blur-[2px]" />
        <R.Content
          onOpenAutoFocus={(e) => focusFirstField(e, onOpenAutoFocus)}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={(e) => !dismissable && e.preventDefault()}
          onPointerDownOutside={(e) => !dismissable && e.preventDefault()}
          onInteractOutside={(e) => !dismissable && e.preventDefault()}
          className={cx(
            "pop fixed left-1/2 z-50 flex w-[calc(100vw-2rem)] -translate-x-1/2 flex-col overflow-hidden",
            align === "top"
              ? "hx-dialog-top top-[6vh] max-h-[calc(100dvh-12vh)]"
              : "hx-dialog top-1/2 max-h-[calc(100dvh-2rem)] -translate-y-1/2",
            width,
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-5.5 pb-1">
            <div className="min-w-0 pt-0.5">
              <R.Title className="display text-[21px] leading-tight">{title}</R.Title>
              {description ? (
                <R.Description className="mt-1.5 text-[14px] leading-relaxed text-body">{description}</R.Description>
              ) : (
                <R.Description className="sr-only">{typeof title === "string" ? title : "Dialog"}</R.Description>
              )}
            </div>
            {dismissable ? <CloseButton /> : null}
          </div>
          <div data-dialog-body className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-6">
            {children}
          </div>
          {footer ? (
            <div data-toast-lift className="flex flex-wrap items-center justify-end gap-2.5 border-t border-line bg-panel/60 px-6 py-4">{footer}</div>
          ) : null}
        </R.Content>
      </R.Portal>
    </R.Root>
  );
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissable = true,
  side = "right",
  className,
  kicker,
  onOpenAutoFocus,
}: BaseProps & { side?: "right" | "left"; kicker?: ReactNode }) {
  return (
    <R.Root open={open} onOpenChange={onOpenChange}>
      <R.Portal>
        <R.Overlay className="hx-overlay fixed inset-0 z-50 bg-[var(--hx-overlay)]" />
        <R.Content
          onOpenAutoFocus={(e) => focusFirstField(e, onOpenAutoFocus)}
          onEscapeKeyDown={(e) => !dismissable && e.preventDefault()}
          onPointerDownOutside={(e) => !dismissable && e.preventDefault()}
          onInteractOutside={(e) => !dismissable && e.preventDefault()}
          className={cx(
            "fixed inset-y-0 z-50 flex w-full flex-col bg-raised shadow-[var(--hx-shadow-pop)] sm:inset-y-2 sm:rounded-[var(--radius-card)]",
            side === "right" ? "hx-sheet-right right-0 sm:right-2" : "hx-sheet-left left-0 sm:left-2",
            className ?? "sm:max-w-[560px]",
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-6 pt-5 pb-4">
            <div className="min-w-0">
              {kicker ? <div className="kicker mb-2">{kicker}</div> : null}
              <R.Title className="display text-[21px] leading-tight break-words">{title}</R.Title>
              {description ? (
                <R.Description className="mt-1 text-[14px] text-body">{description}</R.Description>
              ) : (
                <R.Description className="sr-only">{typeof title === "string" ? title : "Panel"}</R.Description>
              )}
            </div>
            {dismissable ? <CloseButton /> : null}
          </div>
          <div data-dialog-body className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {children}
          </div>
          {footer ? <div data-toast-lift className="flex flex-wrap items-center gap-2.5 border-t border-line px-6 py-4">{footer}</div> : null}
        </R.Content>
      </R.Portal>
    </R.Root>
  );
}
